import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { APPLICANT_ATTRIBUTION_BOTH, MAIL_CONTENT_KINDS, TEMPLATE_TYPES } from 'cpool-constants';
import type {
    SchemaLoanRequestResolver_Get,
    SchemaLoanRequestResolver_GetSummary,
    SchemaTemplateResolver_GetSignature,
} from 'cpool-tables/backend-resolvers';
import { schemaTemplateResolver_GetSignature } from 'cpool-tables/backend-resolvers';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuLoaderCircle, LuSend } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { SEND_COOLDOWN_MS } from './constants';
import { useMailTemplates } from './hooks/useMailTemplates';
import { MailPreview } from './MailPreview';
import { MailToolbar } from './MailToolbar';
import { OffersSelect } from './OffersSelect';
import type { EmailComposerForm } from './schemas/emailComposerForm.schema';
import { emailSubmit } from './utils/emailSubmit';
import { getEtaMailContent } from './utils/getEtaMailContent';
import { resizeFreetextValues } from './utils/resizeFreetextValues';
import type { InputKind } from '../headerFooterTemplate/types';
import { useAcceptedOffers } from '../leadDetails/components/Overview/OfferManagement/hooks/useAcceptedOffers';
import { useAclPermissions } from '../leadDetails/hooks/useAclPermissions';
import { useOfferTemplateList } from '../templates/hooks/useOfferTemplateList';
import { APICommunicator } from '~/api/APICommunicator';
import { useUserData } from '~/components/Header/menu/hooks/useUserData';
import useCallbackWithErrorHandler from '~/hooks/useCallbackWithErrorHandler';
import useUserInfo from '~/hooks/useUserInfo';

type Props = {
    lead: SchemaLoanRequestResolver_GetSummary[number] | SchemaLoanRequestResolver_Get;
    selectedTemplateId?: string;
    initialOfferId?: string;
    onCancel?: () => void;
    onSuccess?: () => void;
    sendCooldownMs?: number;
    sidebar?: ReactNode;
};

export const EmailComposer: React.FC<Props> = ({
    initialOfferId,
    lead,
    onCancel,
    onSuccess,
    selectedTemplateId,
    sendCooldownMs = SEND_COOLDOWN_MS,
    sidebar,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [contentKind, setContentKind] = useState<InputKind>(MAIL_CONTENT_KINDS.MAIL_HTML_CONTENT);
    const { data: templateList } = useMailTemplates();
    const { data: offerTemplateList } = useOfferTemplateList();
    const user = useUserInfo();
    const { data: userData } = useUserData(user?.id);
    const [mailSignature, setMailSignature] = useState<SchemaTemplateResolver_GetSignature | null>(null);
    const [isCooldownActive, setIsCooldownActive] = useState(false);
    const [formRevision, setFormRevision] = useState(0);
    const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { permissions } = useAclPermissions({ loanRequestId: lead?.id });
    const allTemplates = [...(templateList ?? []), ...(offerTemplateList ?? [])];

    useEffect(() => {
        if (user?.signatureId) {
            APICommunicator.apiFetch({
                route: `/query/template/getSignature?id=${user.signatureId}`,
            })
                .then((response) => response.json())
                .then((data) => {
                    const parsedData = schemaTemplateResolver_GetSignature.parse(data);
                    setMailSignature(parsedData);
                })
                .catch(() => toast.error(t('MESSAGE.EMAIL.SIGNATURE_FETCH_ERROR')));
        }
    }, [t, user]);

    const template = allTemplates?.find((item) => item.id === selectedTemplateId);
    const isOfferTemplate = template?.type === TEMPLATE_TYPES.OFFER_MAIL;
    const templateId = template?.id;
    const previousTemplateId = useRef(templateId);

    const formMethods = useForm<EmailComposerForm>({
        defaultValues: {
            freetext: {
                body: [],
                offers: [],
            },
            offerDocuments: {},
            offers: initialOfferId ? [initialOfferId] : [],
            recipient: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
        },
    });

    const {
        control,
        formState: { isSubmitting },
        getValues,
        handleSubmit,
        register,
        reset,
        setValue,
        watch,
    } = formMethods;

    const { fields } = useFieldArray({
        control,
        name: 'freetext.body',
    });
    const { fields: offerFields } = useFieldArray({
        control,
        name: 'freetext.offers',
    });

    const { data: acceptedOffers } = useAcceptedOffers(lead?.id ?? '');
    const selectedOfferIds = watch('offers') as string[] | undefined;
    const recipient = watch('recipient');
    const previewLead = useMemo<typeof lead>(() => {
        if (recipient !== APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2 || !lead.antragsteller2) return lead;
        return {
            ...lead,
            antragsteller1: lead.antragsteller2,
            antragsteller2: lead.antragsteller1,
        };
    }, [lead, recipient]);

    const selectedOffers = useMemo(() => {
        if (!selectedOfferIds || selectedOfferIds.length === 0 || !acceptedOffers) {
            return [];
        }
        return selectedOfferIds
            .map((id) => acceptedOffers.find((offer) => offer.id === id))
            .filter((offer): offer is NonNullable<typeof offer> => offer !== undefined);
    }, [selectedOfferIds, acceptedOffers]);

    const convertedMailContent = useMemo(
        () =>
            getEtaMailContent({
                contentKind,
                fields,
                lead: previewLead,
                offerFields,
                placeholderText: t('MESSAGE.EMAIL.FREETEXT_PLACEHOLDER'),
                register,
                selectedOffers,
                template,
                user: userData,
            }),
        [contentKind, fields, offerFields, previewLead, t, register, template, userData, selectedOffers],
    );

    const { freetextBodyCount, freetextOffersCount } = useMemo(() => {
        const countFreetextFields = (content: string) => (content.match(/it\.freitext/gm) || []).length;

        const bodyCount = Math.max(
            countFreetextFields(template?.data.mailContent ?? ''),
            countFreetextFields(template?.data.mailHTMLContent ?? ''),
        );

        if (template?.type !== TEMPLATE_TYPES.OFFER_MAIL) {
            return { freetextBodyCount: bodyCount, freetextOffersCount: 0 };
        }

        const countPerOffer = Math.max(
            (template.data.offerItemContent?.match(/it\.freitext/gm) || []).length,
            (template.data.offerItemHTMLContent?.match(/it\.freitext/gm) || []).length,
        );

        return { freetextBodyCount: bodyCount, freetextOffersCount: countPerOffer * selectedOffers.length };
    }, [template, selectedOffers.length]);

    // NOTE: this effect must never depend on the template object itself. The template queries refetch regularly and
    // their schema coerces createdAt into a Date, which react-query's structural sharing cannot deduplicate. Every
    // refetch therefore hands us a new template reference even when nothing changed, and resetting on that reference
    // would wipe everything the user has typed into the freetext fields.
    useEffect(() => {
        const { freetext, offerDocuments, offers, recipient: currentRecipient } = getValues();
        const isTemplateSwitched = previousTemplateId.current !== templateId;
        previousTemplateId.current = templateId;

        const currentOffers = offers || [];

        let offersValue: string[] = [];
        if (currentOffers.length > 0) {
            offersValue = currentOffers;
        } else if (initialOfferId) {
            offersValue = [initialOfferId];
        }

        reset({
            freetext: {
                body: resizeFreetextValues(isTemplateSwitched ? undefined : freetext.body, freetextBodyCount),
                offers: resizeFreetextValues(isTemplateSwitched ? undefined : freetext.offers, freetextOffersCount),
            },
            offerDocuments: isTemplateSwitched ? {} : offerDocuments ?? {},
            offers: offersValue,
            recipient: currentRecipient,
        });
        setFormRevision((revision) => revision + 1);
    }, [templateId, freetextBodyCount, freetextOffersCount, reset, initialOfferId, getValues]);

    const clearCooldown = useCallback(() => {
        if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
        setIsCooldownActive(false);
    }, []);

    const startCooldown = useCallback(() => {
        if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
        setIsCooldownActive(true);
        cooldownTimerRef.current = setTimeout(() => {
            cooldownTimerRef.current = null;
            setIsCooldownActive(false);
        }, sendCooldownMs);
    }, [sendCooldownMs]);

    useEffect(
        () => () => {
            if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
        },
        [],
    );

    const onSubmit: SubmitHandler<EmailComposerForm> = useCallbackWithErrorHandler(
        async (values: EmailComposerForm) => {
            startCooldown();
            try {
                const response = await emailSubmit({
                    lead,
                    queryClient,
                    successMessage: t('MESSAGE.EMAIL.SENT'),
                    template,
                })(values);
                if (!(response instanceof Response) || !response.ok) clearCooldown();
                else onSuccess?.();
                return response;
            } catch (error) {
                clearCooldown();
                throw error;
            }
        },
        [clearCooldown, lead, onSuccess, queryClient, startCooldown, template, t],
        t('MESSAGE.EMAIL.ERROR'),
    );

    const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
        if (event.key === 'Enter') event.preventDefault();
    };

    const isOfferSelectionMissing = isOfferTemplate && (selectedOfferIds?.length ?? 0) === 0;
    const isSendEmailMessageDisabled =
        !permissions.api.sendEmailMessage || isSubmitting || isCooldownActive || isOfferSelectionMissing;
    const applicant2Name = [lead.antragsteller2?.personendaten.vorname, lead.antragsteller2?.personendaten.nachname]
        .filter(Boolean)
        .join(' ');

    if (!template) return null;
    return (
        <FormProvider {...formMethods}>
            <form
                className="flex h-full w-full min-h-0 min-w-0 flex-col bg-white"
                onKeyDown={handleKeyDown}
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="flex min-h-0 flex-1 gap-3">
                    {sidebar}
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md bg-surface-light">
                        <MailToolbar
                            canMailSecondApplicant={lead.antragsteller2?.dataProtectionAgreed === true}
                            contentKind={contentKind}
                            hasSecondApplicant={Boolean(lead.antragsteller2)}
                            onContentKindChange={setContentKind}
                            onRecipientChange={(value) => setValue('recipient', value)}
                            recipient={recipient}
                            secondApplicantName={applicant2Name}
                            templateName={template.name}
                        />
                        <div className="flex min-h-0 flex-1 flex-col gap-3 bg-surface-light p-[18px] pt-3">
                            <MailPreview
                                content={convertedMailContent}
                                contentKind={contentKind}
                                formRevision={formRevision}
                                getValues={getValues}
                                setValue={setValue}
                                signature={mailSignature}
                                subject={template.data.subject}
                                templateId={templateId}
                                user={userData}
                            />
                            {isOfferTemplate && !!lead?.id && (
                                <div className="min-h-0 max-h-[50%] shrink-0 overflow-hidden">
                                    <OffersSelect
                                        leadId={lead.id}
                                        name="offers"
                                        preSelectedOfferId={initialOfferId}
                                        hasSecondApplicant={Boolean(lead.antragsteller2)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex h-12 shrink-0 items-end justify-end gap-2">
                    <Button
                        type="button"
                        className="w-[110px]"
                        variant={ButtonVariant.SECONDARY}
                        onClick={() => onCancel?.()}
                    >
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button
                        type="submit"
                        className="h-8 w-[131px] whitespace-nowrap"
                        disabled={isSendEmailMessageDisabled}
                        iconLeft={
                            isSubmitting ? (
                                <LuLoaderCircle className="size-4 animate-spin" />
                            ) : (
                                <LuSend className="size-4" data-testid="send-icon" />
                            )
                        }
                    >
                        {t('MESSAGE.EMAIL.SEND')}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};
